import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { getCompanySubmissionsBlackListDataTableObject } from './utils/company-submissions-black-list-data-table-object';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { DrawerObject } from '@/components/drawer/drawer.types';
import { DrawerComponent } from '@/components/drawer/drawer.component';
import { companySubmissionsBlackListAddDrawerObject } from './utils/company-submission-black-list-add-drawer-object';
import { getCompanySubmissionsBlackListAddFormObject } from './utils/company-submission-black-list-form-object';
import { CompanyBlackListRepository } from '@/store/company-blacklist-state/company-blacklist-state.repository';
import { CompanyBlackListService } from './company-submissions-blacklist.service';
import { companyBlackListSchema } from '@/validations/blacklist-company.validation';
import { SelectOption } from '@/components/form-builder/form-builder.types';
import { CompanyBlackList, CompanyBlackListDto, DecisionEnum } from '@/types';
import { DataTableRowActions } from '@/components/data-table-builder/data-table.types';
import { getCompanySubmissionsBlackListUpdateDrawerObject } from './utils/company-submission-black-list-update-drawer-object';
import { getCompanySubmissionsBlackListUpdateFormObject } from './utils/company-submission-black-list-update-form-object';

@Component({
  selector: 'sm-company-submissions-blacklist',
  imports: [PageHeaderComponent, DataTableBuilderComponent, DrawerComponent],
  templateUrl: './company-submissions-blacklist.component.html',
  styleUrl: './company-submissions-blacklist.component.scss',
})
export class CompanySubmissionsBlacklistComponent implements OnInit {
  companyBlackListService = inject(CompanyBlackListService);
  companyBlackListRepository = inject(CompanyBlackListRepository);
  translate = inject(TranslateService);
  messageService = inject(MessageService);

  breadcrumb = [
    {
      label: 'settings.companyRequests.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.blackList.title',
      routerLink: ['/settings/company-submissions-blacklist'],
    },
  ];

  @ViewChild('addBlackList') drawerComponent!: DrawerComponent;
  addDrawer?: DrawerObject;

  @ViewChild('updateBlackList') updateDrawerComponent!: DrawerComponent;
  updateDrawer?: DrawerObject;

  formObject = getCompanySubmissionsBlackListAddFormObject({
    store: this.companyBlackListRepository,
    companyOptions: [],
  });

  dataTableObject = getCompanySubmissionsBlackListDataTableObject({
    store: this.companyBlackListRepository,
    createAction: () => {
      this.addDrawer = companySubmissionsBlackListAddDrawerObject({
        formObject: this.formObject,
        toggleVisibility: () => this.drawerComponent.toggleVisible(),
        action: () => this.add(),
      });
      this.drawerComponent.toggleVisible();
    },
  });

  data: CompanyBlackList[] = [];
  totalRecords = 0;
  loading = false;

  rowActions: DataTableRowActions = {
    editAction: (row: any) => {
      this.updateDrawer = getCompanySubmissionsBlackListUpdateDrawerObject({
        formObject: getCompanySubmissionsBlackListUpdateFormObject({
          store: this.companyBlackListRepository,
          companyOptions: [
            {
              id: row.id,
              nom: row.nom,
              nif: row.nif,
              pays: row.pays,
            },
          ],
        }),
        action: () => this.update(),
        toggleVisibility: () => this.updateDrawerComponent.toggleVisible(),
      });
      this.updateDrawerComponent.toggleVisible();
    },
  };

  ngOnInit() {
    this.loadData();

    this.dataTableObject = getCompanySubmissionsBlackListDataTableObject({
      store: this.companyBlackListRepository,
      createAction: () => {
        this.addDrawer = companySubmissionsBlackListAddDrawerObject({
          formObject: this.formObject,
          toggleVisibility: () => this.drawerComponent.toggleVisible(),
          action: () => this.add(),
        });
        this.drawerComponent.toggleVisible();
      },
      rowActions: this.rowActions,
    });
  }

  add() {
    const dto: CompanyBlackListDto = {
      ...(this.companyBlackListRepository.get('dto') || {}),
      natureDecision: this.companyBlackListRepository.getNested<SelectOption>('dto', 'natureDecision').code as DecisionEnum,
    };
    const result = companyBlackListSchema.safeParse(dto);
    if (result.success) {
      this.companyBlackListService.create(dto).subscribe({
        next: () => {
          this.loadData();
          this.companyBlackListRepository.reset();
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.blackList.message.success.added'),
          });
          this.drawerComponent.toggleVisible();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.blackList.message.error.added'),
          });
        },
      });
    } else {
      this.companyBlackListRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  update() {
    const dto: CompanyBlackListDto = {
      ...(this.companyBlackListRepository.get('dto') || {}),
      natureDecision: this.companyBlackListRepository.getNested<SelectOption>('dto', 'natureDecision').code as DecisionEnum,
    };
    const id = this.companyBlackListRepository.getNested<number>('dto', 'id');
    const result = companyBlackListSchema.safeParse(dto);
    if (result.success) {
      this.companyBlackListService.update(id, dto).subscribe({
        next: () => {
          this.loadData();
          this.companyBlackListRepository.reset();
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.blackList.message.success.updated'),
          });
          this.updateDrawerComponent.toggleVisible();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.blackList.message.error.updated'),
          });
        },
      });
    } else {
      this.companyBlackListRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  loadData() {
    this.loading = true;
    this.companyBlackListService.getCompanies().subscribe({
      next: data => {
        this.formObject = getCompanySubmissionsBlackListAddFormObject({
          store: this.companyBlackListRepository,
          companyOptions: data,
        });
      },
    });
    this.companyBlackListService.getBlackListedCompanies().subscribe({
      next: data => {
        this.loading = false;
        this.data = data.content;
        this.totalRecords = data.totalElements;
      },
      error: (error: any) => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: error.error.message || this.translate.instant('settings.blackList.message.error.loading'),
        });
      },
    });
  }
}
